<template>
  <div class="p-collapsible min-w-0" :data-expanded="modelValue">
    <div v-if="!modelValue && $slots.collapsed" @click.left="onHeaderClick">
      <slot name="collapsed" :toggle="toggle" />
      <PButton
        variant="ghost"
        size="sm"
        class="sr-only text-inherit! dark:text-inherit!"
        :disabled="disabled"
        :aria-expanded="false"
        :aria-label="`Uitvouwen${title ? `: ${title}` : ''}`"
        @click.stop="toggle"
      >
        Uitvouwen
      </PButton>
    </div>
    <PCard
      v-else
      :size="size"
      :color="color"
      :hoverable="false"
      :variant="variant === 'inset' ? 'soft' : 'outline'"
      class="divide-y-0! overflow-visible!"
      :class="{ 'border-0!': !bordered }"
    >
      <template v-if="!hideHeader" #header>
        <div
          class="flex items-center gap-4 select-none"
          :class="[
            headerSpacing,
            getHeaderRounding(),
            headerClass ||
              (headerVariant === 'soft' || (headerVariant === 'auto' && !modelValue)
                ? 'gray-lighten-2'
                : 'primary'),
            {
              'cursor-pointer': !disabled,
              'hover:bg-primary hover:text-on-primary dark:hover:bg-primary-dark dark:hover:text-on-primary-dark':
                headerVariant === 'auto' && !modelValue && !disabled && !headerClass
            }
          ]"
          @click.left="onHeaderClick"
        >
          <div :id="`${id}-title`" class="min-w-0 grow">
            <slot name="header" :expanded="modelValue"
              ><span class="font-heading">{{ title }}</span></slot
            >
          </div>
          <PButton
            variant="ghost"
            size="sm"
            class="focus-visible:outline-secondary shrink-0 text-inherit! focus-visible:outline-2 dark:text-inherit!"
            :class="{ 'sr-only focus:not-sr-only': !modelValue && !showCollapsedIcon }"
            :disabled="disabled"
            :aria-expanded="modelValue"
            :aria-controls="`${id}-content`"
            :aria-label="`${modelValue ? 'Invouwen' : 'Uitvouwen'}${title ? `: ${title}` : ''}`"
            :title="modelValue ? 'Invouwen' : 'Uitvouwen'"
            @click.stop="toggle"
          >
            <FontAwesomeIcon
              :icon="faChevronDown"
              class="transition-transform duration-150 motion-reduce:transition-none"
              :class="{ 'rotate-180': modelValue }"
            />
          </PButton>
        </div>
      </template>
      <template v-if="hasBody()" #default>
        <div v-if="!modelValue && $slots.summary" @click.left="onHeaderClick">
          <slot name="summary" />
        </div>
        <div v-if="$slots.preview">
          <slot name="preview" :expanded="modelValue" />
        </div>
        <div
          v-if="modelValue && $slots.default"
          :id="`${id}-content`"
          role="region"
          :class="{ 'mt-2': $slots.preview }"
          :aria-labelledby="!hideHeader ? `${id}-title` : undefined"
          :aria-label="hideHeader ? title : undefined"
        >
          <slot :expanded="modelValue" :toggle="toggle" />
        </div>
      </template>
    </PCard>
  </div>
</template>

<script setup lang="ts">
import { computed, useId, type VNode } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import PCard from "./PCard.vue"
import PButton from "./PButton.vue"
import type { ComponentColor } from "@/config/component-colors"

const props = withDefaults(
  defineProps<{
    /** Controlled by the parent; the list decides whether multiple panels may be open. */
    modelValue?: boolean
    title?: string
    color?: ComponentColor
    size?: "sm" | "md" | "lg"
    disabled?: boolean
    hideHeader?: boolean
    /** Standard card or the inset instruction editor. */
    variant?: "card" | "inset"
    /** Auto is soft when collapsed and solid on hover or expansion. */
    headerVariant?: "auto" | "solid" | "soft"
    /** Show the outer card border. */
    bordered?: boolean
    showCollapsedIcon?: boolean
    headerClass?: string
  }>(),
  {
    modelValue: false,
    color: "primary",
    size: "sm",
    disabled: false,
    hideHeader: false,
    variant: "card",
    headerVariant: "auto",
    bordered: true,
    showCollapsedIcon: true
  }
)
const emit = defineEmits<{
  "update:modelValue": [expanded: boolean]
  toggle: [event: MouseEvent]
}>()
const slots = defineSlots<{
  header?(props: { expanded: boolean }): VNode[]
  summary?(): VNode[]
  preview?(props: { expanded: boolean }): VNode[]
  /** Replaces the entire collapsed card, including its header, summary and preview. */
  collapsed?(props: { toggle: (event: MouseEvent) => void }): VNode[]
  default?(props: { expanded: boolean; toggle: (event: MouseEvent) => void }): VNode[]
}>()
const id = useId()
function hasBody() {
  return (
    (!props.modelValue && !!slots.summary) ||
    !!slots.preview ||
    (props.modelValue && !!slots.default)
  )
}
function getHeaderRounding() {
  if (props.bordered && props.variant !== "inset") {
    return hasBody()
      ? "rounded-t-[calc(var(--radius-md)-2px)]"
      : "rounded-[calc(var(--radius-md)-2px)]"
  }
  return hasBody() ? "rounded-t-md" : "rounded-md"
}
const headerSpacing = computed(
  () =>
    ({
      sm: "-m-2 px-2 py-1.5",
      md: "-m-4 px-4 py-3",
      lg: "-m-5 px-5 py-4 sm:-m-6 sm:px-6"
    })[props.size]
)

function toggle(event: MouseEvent) {
  if (props.disabled) return
  event.stopPropagation()
  emit("update:modelValue", !props.modelValue)
  emit("toggle", event)
}

function onHeaderClick(event: MouseEvent) {
  // Selection, links and header actions keep their own behavior.
  if (
    event.target instanceof Element &&
    event.target.closest("button, a, input, select, textarea, [role='button']")
  )
    return
  toggle(event)
}
</script>
